import { useEffect, useMemo, useState, type ReactNode } from 'react'
import {
  FileSpreadsheet,
  TrendingDown,
  Layers,
  Gauge,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  ChevronDown,
  Settings2,
  TrendingUp,
} from 'lucide-react'
import { useUnderwritingModelStore } from '@/store/underwritingModelStore'
import { useLocalAuth } from '@/auth/LocalAuthContext'
import { ANTARCTICA_DEALS } from '@/data/clientMockData'
import { useModuleLoans } from '@/hooks/useDemoDataAccess'
import type { AssetClass } from '@/types'
import AssetClassSelector from '@/components/underwriting/AssetClassSelector'
import { assetClassForDeal } from '@/components/underwriting/assetClassForDeal'
import UnderwritingModelRenderer from '@/components/underwriting/UnderwritingModelRenderer'
import ModelLockBanner from '@/components/underwriting/ModelLockBanner'
import ModelEditorPanel from '@/components/underwriting/ModelEditorPanel'
import { chartPassFailColor, chartStackSegmentColor } from '@/config/chartTheme'
import PreDealValuation from '@/components/underwriting/PreDealValuation'
import SpreadsheetImportExport from '@/components/underwriting/SpreadsheetImportExport'
import {
  cashFlowsOf,
  ltvFraction,
  marketOf,
  operatingStatementOf,
  propertyValueOf,
  recordedNoiOf,
  statedSourcesUses,
  toMillions,
  amountUnitOf,
  underwritingBook,
  type OperatingStatement,
  type StatedSourcesUses,
} from '@/pages/liveModuleData'

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const fmt = (v: number, decimals = 2) => `$${v.toFixed(decimals)}M`
const pct = (v: number, decimals = 1) => `${(v * 100).toFixed(decimals)}%`
const bps = (v: number) => `${(v * 100).toFixed(0)}bp`

function passBadge(pass: boolean, marginal?: boolean) {
  if (pass && !marginal)
    return <span className="inline-flex items-center gap-1 text-emerald-400 text-xs font-medium"><CheckCircle2 size={12} /> Pass</span>
  if (marginal)
    return <span className="inline-flex items-center gap-1 text-amber-400 text-xs font-medium"><AlertTriangle size={12} /> Marginal</span>
  return <span className="inline-flex items-center gap-1 text-red-400 text-xs font-medium"><XCircle size={12} /> Fail</span>
}

/* ------------------------------------------------------------------ */
/*  Eligible deals for underwriting (Application Back+)                */
/* ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ */
/*  Tabs                                                               */
/* ------------------------------------------------------------------ */

type UWTab = 'analysis' | 'model' | 'valuation'

function ModuleShell({ children }: { children: ReactNode }) {
  return (
    <div className="p-4">
      <div className="panel">
        <div className="panel-body text-stone-400 text-sm">{children}</div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function Underwriting() {
  const { localUser } = useLocalAuth()
  const { loans, isLoading, isLive } = useModuleLoans('underwriting', ANTARCTICA_DEALS)
  const uwDeals = useMemo(
    () => underwritingBook(loans, { live: isLive }),
    [loans, isLive],
  )
  const [selectedDealId, setSelectedDealId] = useState('')
  const [activeTab, setActiveTab] = useState<UWTab>('analysis')

  useEffect(() => {
    if (!uwDeals.some((d) => d.id === selectedDealId)) {
      setSelectedDealId(uwDeals[0]?.id ?? '')
    }
  }, [uwDeals, selectedDealId])

  const {
    activeAssetClass,
    setActiveAssetClass,
    getActiveModel,
    dealData,
    updateField,
    editorOpen,
    toggleEditor,
    lockModel,
    unlockModel,
    addFieldToModel,
    removeFieldFromModel,
  } = useUnderwritingModelStore()

  const deal = useMemo(
    () => uwDeals.find((d) => d.id === selectedDealId) ?? uwDeals[0],
    [selectedDealId, uwDeals],
  )
  // The model follows the selected deal's own asset class (nd3-25); a person
  // may still pick another tab for the deal they are looking at.
  const dealAssetClass = assetClassForDeal(deal?.type)
  // The deal a person chose a model for by hand. A deal whose class the
  // library has no model for (Mixed-Use, Data Center) used to keep whatever
  // model the previous deal selected — Industrial over Lakeshore Commons —
  // with nothing to say it was not the deal's own (nd4-values-7).
  const [pickedFor, setPickedFor] = useState<string | null>(null)
  useEffect(() => {
    if (dealAssetClass) setActiveAssetClass(dealAssetClass)
    setPickedFor(null)
  }, [deal?.id, dealAssetClass, setActiveAssetClass])
  const noLibraryModel = !dealAssetClass && pickedFor !== (deal?.id ?? null)
  const model = noLibraryModel ? null : getActiveModel()

  if (isLoading) {
    return <ModuleShell>Loading underwriting book…</ModuleShell>
  }
  if (!deal) {
    return (
      <ModuleShell>
        <p>
          {isLive
            ? 'No live deals are on the underwriting book.'
            : 'No deals available for underwriting.'}
        </p>
      </ModuleShell>
    )
  }

  const cf = cashFlowsOf(deal)
  const market = marketOf(deal)
  const stressTests = deal.stressTests ?? []
  const amountUnit = amountUnitOf(deal)
  const loanAmountMm = toMillions(deal.loanAmount, amountUnit)

  /* ---- derived operating statement numbers ---- */
  const grossRent = cf.rents
  const otherIncome = cf.revenue - cf.rents
  const vacancyRate = market.vacRate / 100
  const vacancyLoss = cf.revenue * vacancyRate
  const egi = cf.revenue - vacancyLoss

  const totalExpenses = cf.expenses
  const reTaxes = totalExpenses * 0.35
  const insurance = totalExpenses * 0.10
  const mgmtFee = totalExpenses * 0.20
  const repairs = totalExpenses * 0.15
  const otherExp = totalExpenses * 0.20
  const uwNoi = isLive
    ? ((n) => (n != null ? toMillions(n, amountUnit) : 0))(recordedNoiOf(deal))
    : cf.prospectiveNOI || toMillions(deal.noi, amountUnit)
  // Live: the statement exactly as the record holds it (MOD-09).
  const statement = isLive ? operatingStatementOf(deal) : null
  const stated = isLive ? statedSourcesUses(deal) : null

  /* ---- capital structure ---- */
  const stack = (deal.capitalStack ?? []).map((layer) => ({
    ...layer,
    amount: toMillions(layer.amount, amountUnit),
  }))
  const totalDebt = stack.reduce((s, l) => s + l.amount, 0) || loanAmountMm
  // The value on record, or a value the platform derived under a current
  // computation (a component valuation), labelled Derived with its formula.
  const valueReading = propertyValueOf(deal)
  const recordedValue = valueReading != null ? toMillions(valueReading.value, amountUnit) : 0
  const valueFormula = valueReading?.state === 'derived' ? valueReading.formula : null
  const ltvKnown = typeof deal.ltv === 'number' && Number.isFinite(deal.ltv)
  const dscrKnown = typeof deal.dscr === 'number' && Number.isFinite(deal.dscr)
  const ltv = ltvKnown ? (ltvFraction(deal.ltv) ?? 0) : 0
  // The value on record, else none — live and demo alike. R3: a stated LTV is
  // its own sourced figure and never back-solves value — loan ÷ LTV gave
  // Senate Row a 4.8% cap rate and a $108.01M "reconciled" value it never
  // states, and gave every demo deal without a value an "Acquisition" price.
  const pv = recordedValue
  const closingCosts = isLive ? 0 : pv * 0.03
  const reserves = isLive ? 0 : pv * 0.005
  const liveUsesTotal = stated ? stated.totalCost ?? stated.purchasePrice : null
  const totalUses = isLive ? liveUsesTotal ?? 0 : pv + closingCosts + reserves
  const equity = isLive ? stated?.equity ?? 0 : Math.max(totalUses - totalDebt, 0)

  const seniorLayer = stack.find((l) => l.type === 'Senior Mortgage' || l.type === 'Large Loan' || l.type === 'Ground Lease')
  const mezzLayer = stack.find((l) => l.type === 'Mezzanine' || l.type === 'B-Note')

  /* ---- metrics ---- */
  const dscr = dscrKnown ? deal.dscr : 0
  const dyKnown = typeof deal.debtYield === 'number' && Number.isFinite(deal.debtYield)
  const dy = dyKnown ? deal.debtYield as number : (loanAmountMm > 0 && uwNoi > 0 ? uwNoi / loanAmountMm : 0)
  const capRate = pv > 0 && uwNoi > 0 ? uwNoi / pv : 0
  const noiMargin = statement
    ? statement.egi && statement.noi != null ? statement.noi / statement.egi : 0
    : cf.revenue > 0 ? uwNoi / cf.revenue : 0
  const estIrr = isLive ? null : 0.08 + (dscr - 1.0) * 0.15 + (dy > 0.08 ? 0.02 : 0)

  const ltvPass = ltvKnown && ltv <= 0.75
  const dscrPass = dscrKnown && dscr >= 1.25
  // No loan amount (Cranbury) or no NOI: no debt yield to test, not a failing 0.0%.
  const dyAvailable = dy > 0
  const dyPass = dyAvailable && dy >= 0.08

  const tabs: { id: UWTab; label: string; icon: React.ReactNode }[] = [
    { id: 'analysis', label: 'Analysis', icon: <FileSpreadsheet size={12} /> },
    { id: 'model', label: 'Model Input', icon: <Settings2 size={12} /> },
    { id: 'valuation', label: 'Pre-Deal Valuation', icon: <TrendingUp size={12} /> },
  ]

  return (
    <div className="space-y-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-amber-400/10 border border-amber-400/20">
            <FileSpreadsheet size={16} className="text-amber-300" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-stone-100">Underwriting</h1>
            <p className="text-xs text-stone-500">Asset-class-specific cash flow analysis, stress testing, and credit metrics</p>
          </div>
        </div>

        {/* Deal Selector */}
        <div className="relative">
          <select
            className="input pr-8 text-sm min-w-[260px]"
            value={selectedDealId}
            onChange={(e) => setSelectedDealId(e.target.value)}
          >
            {uwDeals.map((d) => (
              <option key={d.id} value={d.id}>
                {d.id} - {d.property} ({d.stage})
              </option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-stone-500 pointer-events-none" />
        </div>
      </div>

      {/* Asset Class Selector */}
      <AssetClassSelector
        selected={noLibraryModel ? null : activeAssetClass}
        onSelect={(ac: AssetClass) => {
          setActiveAssetClass(ac)
          setPickedFor(deal.id)
        }}
      />
      {noLibraryModel && (
        <p className="text-[11px] text-stone-400">
          {deal.type
            ? `No ${deal.type} model in the library. `
            : 'This deal records no asset class. '}
          No model is applied until you choose one above.
        </p>
      )}

      {/* Tab Bar */}
      <div className="flex items-center gap-1 border-b border-amber-400/10">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-medium border-b-2 transition-all ${
              activeTab === tab.id
                ? 'border-amber-400 text-amber-300'
                : 'border-transparent text-stone-500 hover:text-stone-300'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Model Lock Banner */}
      {model && (
        <ModelLockBanner
          model={model}
          /* Was hardcoded true: the control claimed a privilege it never
             checked. Unlocking a locked model is a governance action, so it
             follows the same rule as the calculation unlock (register Q5) —
             an administrator or a senior reviewer, never an analyst. */
          canUnlock={Boolean(
            localUser?.is_super_admin ||
            ['admin', 'super_admin', 'senior_underwriter'].includes(String(localUser?.role || ''))
          )}
          onLock={() => lockModel(activeAssetClass, 'Current User')}
          onUnlock={() => unlockModel(activeAssetClass)}
        />
      )}

      {/* Tab Content */}
      {activeTab === 'model' && model && (
        <div className="space-y-4">
          {/* Spreadsheet Import/Export */}
          <SpreadsheetImportExport
            model={model}
            dealId={deal.id}
            values={dealData[deal.id] ?? {}}
            onImport={(data) => {
              Object.entries(data).forEach(([fieldId, value]) => {
                updateField(deal.id, fieldId, value)
              })
            }}
          />

          <div className="grid grid-cols-2 gap-4">
          <UnderwritingModelRenderer
            model={model}
            values={dealData[deal.id] ?? {}}
            onFieldChange={(fieldId, value) => updateField(deal.id, fieldId, value)}
          />
          {editorOpen ? (
            <ModelEditorPanel
              model={model}
              onAddField={(sectionId, field) => addFieldToModel(activeAssetClass, sectionId, field)}
              onRemoveField={(sectionId, fieldId) => removeFieldFromModel(activeAssetClass, sectionId, fieldId)}
              onClose={toggleEditor}
            />
          ) : (
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-stone-700/50 p-8">
              <Settings2 size={24} className="text-stone-600 mb-2" />
              <p className="text-[12px] text-stone-500 mb-3">Customize the {activeAssetClass} underwriting model</p>
              <button
                onClick={toggleEditor}
                className="rounded-lg border border-amber-400/20 bg-amber-400/[0.06] px-4 py-2 text-[11px] font-medium text-amber-300 hover:bg-amber-400/10 transition-colors"
              >
                Open Model Editor
              </button>
            </div>
          )}
          </div>
        </div>
      )}

      {activeTab === 'valuation' && (
        <PreDealValuation
          deal={deal}
          propertyValue={pv}
          valueFormula={valueFormula}
          noi={uwNoi}
          capRate={capRate}
          live={isLive}
        />
      )}

      {activeTab === 'analysis' && (
        <div className="grid grid-cols-2 gap-4">
          {/* ──────────── TOP LEFT: Operating Statement ──────────── */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-header-title flex items-center gap-2">
                <FileSpreadsheet size={12} className="text-amber-400/60" />
                Operating Statement
              </span>
              <span className="text-xs text-stone-500 font-mono">{deal.id}</span>
            </div>
            <div className="panel-body">
              <table className="dense-table">
                <thead>
                  <tr>
                    <th>Line Item</th>
                    <th className="text-right">Amount</th>
                  </tr>
                </thead>
                {statement ? (
                  <LiveStatementRows statement={statement} />
                ) : (
                <tbody>
                  <tr>
                    <td className="text-stone-500 text-xs font-mono uppercase tracking-wider" colSpan={2}>Revenue</td>
                  </tr>
                  <tr>
                    <td className="pl-6 text-stone-300">Gross Rent</td>
                    <td className="text-right text-stone-200 font-mono">{fmt(grossRent)}</td>
                  </tr>
                  <tr>
                    <td className="pl-6 text-stone-300">Other Income</td>
                    <td className="text-right text-stone-200 font-mono">{fmt(otherIncome)}</td>
                  </tr>
                  <tr>
                    <td className="pl-6 text-stone-400">Vacancy Loss ({(vacancyRate * 100).toFixed(1)}%)</td>
                    <td className="text-right text-red-400/80 font-mono">({fmt(vacancyLoss)})</td>
                  </tr>
                  <tr className="border-t border-amber-400/10">
                    <td className="font-medium text-stone-200">Effective Gross Income</td>
                    <td className="text-right font-medium text-amber-300 font-mono">{fmt(egi)}</td>
                  </tr>

                  <tr>
                    <td className="text-stone-500 text-xs font-mono uppercase tracking-wider pt-3" colSpan={2}>Expenses</td>
                  </tr>
                  <tr>
                    <td className="pl-6 text-stone-300">RE Taxes</td>
                    <td className="text-right text-stone-200 font-mono">{fmt(reTaxes)}</td>
                  </tr>
                  <tr>
                    <td className="pl-6 text-stone-300">Insurance</td>
                    <td className="text-right text-stone-200 font-mono">{fmt(insurance)}</td>
                  </tr>
                  <tr>
                    <td className="pl-6 text-stone-300">Management Fee</td>
                    <td className="text-right text-stone-200 font-mono">{fmt(mgmtFee)}</td>
                  </tr>
                  <tr>
                    <td className="pl-6 text-stone-300">Repairs &amp; Maintenance</td>
                    <td className="text-right text-stone-200 font-mono">{fmt(repairs)}</td>
                  </tr>
                  <tr>
                    <td className="pl-6 text-stone-300">Other</td>
                    <td className="text-right text-stone-200 font-mono">{fmt(otherExp)}</td>
                  </tr>
                  <tr className="border-t border-amber-400/10">
                    <td className="text-stone-300">Total Expenses</td>
                    <td className="text-right text-stone-200 font-mono">{fmt(totalExpenses)}</td>
                  </tr>

                  <tr className="border-t-2 border-amber-400/20">
                    <td className="font-semibold text-stone-100">Net Operating Income</td>
                    <td className="text-right font-semibold text-amber-300 font-mono text-base">{fmt(uwNoi)}</td>
                  </tr>
                </tbody>
                )}
              </table>

              <div className="mt-3 pt-3 border-t border-amber-400/10">
                <p className="terminal-kicker mb-1.5">UW Adjustments</p>
                <div className="space-y-1">
                    {isLive ? (
                    <p className="text-xs text-stone-500">UW adjustments are not estimated on the live book.</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-stone-400">Mgmt fee to market (4.5%)</span>
                        <span className="text-amber-400 font-mono">+{fmt(cf.revenue * 0.005)}</span>
                      </div>
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-stone-400">Normalize R&M reserve</span>
                        <span className="text-red-400/80 font-mono">-{fmt(pv * 0.002)}</span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ──────────── TOP RIGHT: Stress Testing ──────────── */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-header-title flex items-center gap-2">
                <TrendingDown size={12} className="text-amber-400/60" />
                Stress Testing
              </span>
            </div>
            <div className="panel-body">
              {stressTests.length > 0 ? (
                <table className="dense-table">
                  <thead>
                    <tr>
                      <th>Scenario</th>
                      {stressTests.map((s) => (
                        <th key={s.name} className="text-center">{s.name}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="text-stone-400">Vacancy Shock</td>
                      {stressTests.map((s) => (
                        <td key={s.name} className="text-center font-mono text-stone-200">
                          {s.vacancyShock === 0 ? '--' : `+${pct(s.vacancyShock, 0)}`}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="text-stone-400">Rent Decline</td>
                      {stressTests.map((s) => (
                        <td key={s.name} className="text-center font-mono text-stone-200">
                          {s.rentDecline === 0 ? '--' : `-${pct(s.rentDecline, 0)}`}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="text-stone-400">Cap Rate Expansion</td>
                      {stressTests.map((s) => (
                        <td key={s.name} className="text-center font-mono text-stone-200">
                          {s.capRateExpansion === 0 ? '--' : `+${bps(s.capRateExpansion)}`}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td colSpan={stressTests.length + 1}>
                        <div className="gold-divider my-1" />
                      </td>
                    </tr>
                    <tr>
                      <td className="font-medium text-stone-200">DSCR</td>
                      {stressTests.map((s) => {
                        const color = s.resultingDscr >= 1.25 ? 'text-emerald-400' : s.resultingDscr >= 1.10 ? 'text-amber-400' : 'text-red-400'
                        return (
                          <td key={s.name} className={`text-center font-mono font-semibold ${color}`}>
                            {s.resultingDscr.toFixed(2)}x
                          </td>
                        )
                      })}
                    </tr>
                    <tr>
                      <td className="font-medium text-stone-200">LTV</td>
                      {stressTests.map((s) => {
                        const color = s.resultingLtv <= 0.75 ? 'text-emerald-400' : s.resultingLtv <= 0.85 ? 'text-amber-400' : 'text-red-400'
                        return (
                          <td key={s.name} className={`text-center font-mono font-semibold ${color}`}>
                            {pct(s.resultingLtv, 0)}
                          </td>
                        )
                      })}
                    </tr>
                    <tr>
                      <td className="font-medium text-stone-200">Debt Yield</td>
                      {stressTests.map((s) => {
                        const color = s.resultingDebtYield >= 0.08 ? 'text-emerald-400' : s.resultingDebtYield >= 0.06 ? 'text-amber-400' : 'text-red-400'
                        return (
                          <td key={s.name} className={`text-center font-mono font-semibold ${color}`}>
                            {pct(s.resultingDebtYield, 1)}
                          </td>
                        )
                      })}
                    </tr>
                    <tr className="border-t border-amber-400/10">
                      <td className="font-medium text-stone-200">Verdict</td>
                      {stressTests.map((s) => {
                        const marginal = s.pass && (s.resultingDscr < 1.25 || s.resultingLtv > 0.75)
                        return (
                          <td key={s.name} className="text-center">
                            {passBadge(s.pass, marginal)}
                          </td>
                        )
                      })}
                    </tr>
                  </tbody>
                </table>
              ) : (
                <p className="text-stone-500 text-sm">No stress tests available for this deal.</p>
              )}
            </div>
          </div>

          {/* ──────────── BOTTOM LEFT: Capital Structure ──────────── */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-header-title flex items-center gap-2">
                <Layers size={12} className="text-amber-400/60" />
                Capital Structure
              </span>
            </div>
            <div className="panel-body">
              <div className="grid grid-cols-2 gap-4">
                {stated ? (
                  <LiveSourcesUses stated={stated} />
                ) : (<>
                <div>
                  <p className="terminal-kicker mb-2">Sources</p>
                  <div className="space-y-1.5">
                    {seniorLayer && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-300">{seniorLayer.type}</span>
                        <span className="font-mono text-stone-200">{fmt(seniorLayer.amount)}</span>
                      </div>
                    )}
                    {mezzLayer && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-stone-300">{mezzLayer.type}</span>
                        <span className="font-mono text-stone-200">{fmt(mezzLayer.amount)}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-300">Sponsor Equity</span>
                      <span className="font-mono text-stone-200">{fmt(equity)}</span>
                    </div>
                    <div className="border-t border-amber-400/20 pt-1 flex items-center justify-between text-sm font-semibold">
                      <span className="text-stone-200">Total</span>
                      <span className="font-mono text-amber-300">{fmt(totalUses)}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <p className="terminal-kicker mb-2">Uses</p>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-300">Acquisition</span>
                      <span className="font-mono text-stone-200">{pv > 0 ? fmt(pv) : '—'}</span>
                    </div>
                    {!isLive && (
                      <>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-stone-300">Closing Costs</span>
                          <span className="font-mono text-stone-200">{pv > 0 ? fmt(closingCosts) : '—'}</span>
                        </div>
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-stone-300">Reserves</span>
                          <span className="font-mono text-stone-200">{pv > 0 ? fmt(reserves) : '—'}</span>
                        </div>
                      </>
                    )}
                    <div className="border-t border-amber-400/20 pt-1 flex items-center justify-between text-sm font-semibold">
                      <span className="text-stone-200">Total</span>
                      <span className="font-mono text-amber-300">{fmt(totalUses)}</span>
                    </div>
                  </div>
                </div>
                </>)}
              </div>

              {!stated && (
              <div className="mt-4">
                <p className="terminal-kicker mb-2">Capital Stack</p>
                <div className="h-7 w-full rounded-md overflow-hidden flex border border-amber-400/10">
                  {totalUses > 0 && seniorLayer && (
                    <div
                      className="flex items-center justify-center text-xs font-semibold text-stone-900"
                      style={{
                        width: `${(seniorLayer.amount / totalUses) * 100}%`,
                        background: chartStackSegmentColor(0),
                      }}
                    >
                      Senior {((seniorLayer.amount / totalUses) * 100).toFixed(0)}%
                    </div>
                  )}
                  {totalUses > 0 && mezzLayer && (
                    <div
                      className="flex items-center justify-center text-xs font-semibold text-stone-100"
                      style={{
                        width: `${(mezzLayer.amount / totalUses) * 100}%`,
                        background: chartStackSegmentColor(1),
                      }}
                    >
                      Mezz {((mezzLayer.amount / totalUses) * 100).toFixed(0)}%
                    </div>
                  )}
                  {totalUses > 0 && (
                    <div
                      className="flex items-center justify-center text-xs font-semibold text-stone-300"
                      style={{
                        width: `${(equity / totalUses) * 100}%`,
                        background: chartStackSegmentColor(0, true),
                      }}
                    >
                      Equity {((equity / totalUses) * 100).toFixed(0)}%
                    </div>
                  )}
                </div>
              </div>
              )}

              <div className="mt-3 pt-3 border-t border-amber-400/10">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  {stack.map((l) => (
                    <div key={l.type} className="flex items-center justify-between text-stone-400">
                      <span>{l.type}</span>
                      <span className="font-mono text-stone-300">{l.rate}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* ──────────── BOTTOM RIGHT: Metrics Summary ──────────── */}
          <div className="panel">
            <div className="panel-header">
              <span className="panel-header-title flex items-center gap-2">
                <Gauge size={12} className="text-amber-400/60" />
                Metrics Summary
              </span>
            </div>
            <div className="panel-body space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="metric-cell text-center">
                  <div className="metric-cell-label">LTV</div>
                  <div className={`metric-cell-value ${!ltvKnown ? 'text-stone-500' : ltvPass ? 'text-emerald-400' : 'text-red-400'}`}>{ltvKnown ? pct(ltv, 1) : '—'}</div>
                  <div className="text-xs text-stone-500 mt-0.5 font-mono">{'\u2264'}75%</div>
                  <div className="mt-1">{!ltvKnown ? null : ltvPass ? <CheckCircle2 size={14} className="text-emerald-400 mx-auto" /> : <XCircle size={14} className="text-red-400 mx-auto" />}</div>
                </div>
                <div className="metric-cell text-center">
                  <div className="metric-cell-label">DSCR</div>
                  <div className={`metric-cell-value ${!dscrKnown ? 'text-stone-500' : dscrPass ? 'text-emerald-400' : 'text-red-400'}`}>{dscrKnown ? `${dscr.toFixed(2)}x` : '—'}</div>
                  <div className="text-xs text-stone-500 mt-0.5 font-mono">{'\u2265'}1.25x</div>
                  <div className="mt-1">{!dscrKnown ? null : dscrPass ? <CheckCircle2 size={14} className="text-emerald-400 mx-auto" /> : <XCircle size={14} className="text-red-400 mx-auto" />}</div>
                </div>
                <div className="metric-cell text-center">
                  <div className="metric-cell-label">Debt Yield</div>
                  <div className={`metric-cell-value ${!dyAvailable ? 'text-stone-500' : dyPass ? 'text-emerald-400' : 'text-red-400'}`}>{dyAvailable ? pct(dy, 1) : '—'}</div>
                  <div className="text-xs text-stone-500 mt-0.5 font-mono">{'\u2265'}8.0%</div>
                  <div className="mt-1">{!dyAvailable ? null : dyPass ? <CheckCircle2 size={14} className="text-emerald-400 mx-auto" /> : <XCircle size={14} className="text-red-400 mx-auto" />}</div>
                </div>
              </div>

              <div>
                <div className="gold-divider mb-3" />
                <div className="grid grid-cols-3 gap-3">
                  <div className="metric-cell">
                    <div className="metric-cell-label">Cap Rate</div>
                    <div className="metric-cell-value">{capRate > 0 ? pct(capRate, 1) : '—'}</div>
                  </div>
                  <div className="metric-cell">
                    <div className="metric-cell-label">NOI Margin</div>
                    <div className="metric-cell-value">{noiMargin > 0 ? pct(noiMargin, 1) : '—'}</div>
                  </div>
                  <div className="metric-cell">
                    <div className="metric-cell-label">IRR (Est)</div>
                    <div className="metric-cell-value">{estIrr == null ? '—' : pct(estIrr, 1)}</div>
                  </div>
                </div>
              </div>

              <div>
                <div className="gold-divider mb-3" />
                <p className="terminal-kicker mb-2">vs Guidelines</p>
                <div className="space-y-2.5">
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-stone-400">LTV</span>
                      <span className="font-mono text-stone-300">{ltvKnown ? `${pct(ltv, 0)} / 75% max` : '— / 75% max'}</span>
                    </div>
                    <div className="chart-viz-track chart-viz-bar--rounded h-2 w-full border border-white/10">
                      <div
                        className="chart-viz-bar chart-viz-bar--rounded h-full transition-all duration-500"
                        style={{
                          width: `${Math.min((ltv / 0.75) * 100, 100)}%`,
                          background: chartPassFailColor(ltvPass),
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-stone-400">DSCR</span>
                      <span className="font-mono text-stone-300">{dscrKnown ? `${dscr.toFixed(2)}x / 1.25x min` : '— / 1.25x min'}</span>
                    </div>
                    <div className="chart-viz-track chart-viz-bar--rounded h-2 w-full border border-white/10">
                      <div
                        className="chart-viz-bar chart-viz-bar--rounded h-full transition-all duration-500"
                        style={{
                          width: `${Math.min((dscr / 2.0) * 100, 100)}%`,
                          background: chartPassFailColor(dscrPass),
                        }}
                      />
                    </div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between text-xs mb-1">
                      <span className="text-stone-400">Debt Yield</span>
                      <span className="font-mono text-stone-300">{dyAvailable ? `${pct(dy, 1)} / 8.0% min` : '— / 8.0% min'}</span>
                    </div>
                    <div className="chart-viz-track chart-viz-bar--rounded h-2 w-full border border-white/10">
                      <div
                        className="chart-viz-bar chart-viz-bar--rounded h-full transition-all duration-500"
                        style={{
                          width: `${Math.min((dy / 0.15) * 100, 100)}%`,
                          background: chartPassFailColor(dyPass),
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Live-book panels (MOD-09)                                          */
/* ------------------------------------------------------------------ */

const money = (v: number | null) => (v == null ? '—' : fmt(v))

/** The operating statement exactly as the deal records it. */
function LiveStatementRows({ statement }: { statement: OperatingStatement }) {
  const hasIncome = statement.grossPotentialRent != null || statement.egi != null
  if (!hasIncome && statement.expenses.length === 0 && statement.noi == null) {
    return (
      <tbody>
        <tr>
          <td colSpan={2} className="py-6 text-center text-xs text-stone-500">
            No operating statement is recorded for this deal.
          </td>
        </tr>
      </tbody>
    )
  }
  return (
    <tbody>
      <tr>
        <td className="text-stone-500 text-xs font-mono uppercase tracking-wider" colSpan={2}>Revenue</td>
      </tr>
      <tr>
        <td className="pl-6 text-stone-300">Gross Potential Rent</td>
        <td className="text-right text-stone-200 font-mono">{money(statement.grossPotentialRent)}</td>
      </tr>
      {statement.deductions.map((d) => (
        <tr key={d.label}>
          <td className="pl-6 text-stone-400">{d.label}</td>
          <td className="text-right text-red-400/80 font-mono">({fmt(d.amount)})</td>
        </tr>
      ))}
      {statement.otherIncome != null && (
        <tr>
          <td className="pl-6 text-stone-300">Other Income</td>
          <td className="text-right text-stone-200 font-mono">{fmt(statement.otherIncome)}</td>
        </tr>
      )}
      <tr className="border-t border-amber-400/10">
        <td className="font-medium text-stone-200">
          Effective Gross Income
          {statement.egiDerived && <span className="ml-1 text-[10px] font-normal text-stone-500">derived from the lines above</span>}
        </td>
        <td className="text-right font-medium text-amber-300 font-mono">{money(statement.egi)}</td>
      </tr>
      <tr>
        <td className="text-stone-500 text-xs font-mono uppercase tracking-wider pt-3" colSpan={2}>Expenses</td>
      </tr>
      {statement.expenses.length === 0 && (
        <tr>
          <td className="pl-6 text-stone-500 text-xs" colSpan={2}>Expense lines are not recorded.</td>
        </tr>
      )}
      {statement.expenses.map((line, i) => (
        <tr key={`${i}-${line.label}`}>
          <td className="pl-6 text-stone-300">{line.label}</td>
          <td className="text-right text-stone-200 font-mono">{fmt(line.amount)}</td>
        </tr>
      ))}
      <tr className="border-t border-amber-400/10">
        <td className="text-stone-300">
          Total Expenses
          {statement.totalExpensesDerived && <span className="ml-1 text-[10px] text-stone-500">derived: EGI − NOI</span>}
        </td>
        <td className="text-right text-stone-200 font-mono">{money(statement.totalExpenses)}</td>
      </tr>
      <tr className="border-t-2 border-amber-400/20">
        <td className="font-semibold text-stone-100">Net Operating Income</td>
        <td className="text-right font-semibold text-amber-300 font-mono text-base">{money(statement.noi)}</td>
      </tr>
      <tr>
        <td className="pl-6 text-stone-400">Occupancy</td>
        <td className="text-right text-stone-300 font-mono">{statement.occupancy == null ? '—' : pct(statement.occupancy, 1)}</td>
      </tr>
    </tbody>
  )
}

/** Sources & Uses as the package states them. */
function LiveSourcesUses({ stated }: { stated: StatedSourcesUses }) {
  const usesTotal = stated.totalCost ?? stated.purchasePrice
  const otherCosts = stated.totalCost != null && stated.purchasePrice != null ? stated.totalCost - stated.purchasePrice : null
  const sourcesTotal = stated.equity != null && stated.loan != null ? stated.loan + stated.equity : null
  const row = (label: string, value: string, note?: string) => (
    <div className="flex items-center justify-between text-sm">
      <span className="text-stone-300">
        {label}
        {note && <span className="ml-1 text-[10px] text-stone-500">{note}</span>}
      </span>
      <span className="font-mono text-stone-200">{value}</span>
    </div>
  )
  return (
    <>
      <div>
        <p className="terminal-kicker mb-2">Sources</p>
        <div className="space-y-1.5">
          {row('Senior loan', stated.loan == null ? '—' : fmt(stated.loan), stated.loanIsFull ? 'full loan amount' : undefined)}
          {row('Sponsor equity', money(stated.equity), stated.equityDerived ? `derived: total cost − ${stated.loanIsFull ? 'full loan amount' : 'loan'}` : undefined)}
          <div className="border-t border-amber-400/20 pt-1 flex items-center justify-between text-sm font-semibold">
            <span className="text-stone-200">Total</span>
            <span className="font-mono text-amber-300">{money(sourcesTotal)}</span>
          </div>
        </div>
      </div>
      <div>
        <p className="terminal-kicker mb-2">Uses</p>
        <div className="space-y-1.5">
          {row('Purchase price', money(stated.purchasePrice))}
          {otherCosts != null && otherCosts > 0 && row('Other project costs', fmt(otherCosts), 'total cost − price')}
          <div className="border-t border-amber-400/20 pt-1 flex items-center justify-between text-sm font-semibold">
            <span className="text-stone-200">Total</span>
            <span className="font-mono text-amber-300">{money(usesTotal)}</span>
          </div>
          {usesTotal == null && (
            <p className="text-[10px] text-stone-500">No purchase price or total cost is stated for this deal.</p>
          )}
          {usesTotal != null && sourcesTotal != null && Math.abs(usesTotal - sourcesTotal) >= 0.005 && (
            // R4: a variance between stated figures is shown, not reconciled away.
            <p className="text-[10px] text-amber-300/80">
              Stated sources and uses differ by {fmt(Math.abs(usesTotal - sourcesTotal))}; shown as stated.
            </p>
          )}
        </div>
      </div>
    </>
  )
}

